import { CurrencyPipe, formatCurrency } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart, ApexDataLabels,
  ApexLegend, ApexNonAxisChartSeries,
  ApexOptions, ApexPlotOptions,
  ApexResponsive, ApexStroke,
  ApexTheme, ApexTooltip
} from 'ng-apexcharts';
import { RbacService } from 'src/app/services/rbac.service';
import { CatalogFilter, CatalogService } from 'src/app/services/catalog.service';
import { MapGoogleService } from 'src/app/services/map-google.service';
import { MapService } from 'src/app/services/map.service';
import { enumerators } from 'src/dictionaries/enum.pt-br';
import { SignalingTypeEnum, TaskTypeEnum } from 'src/enumerators';
import { Task } from 'src/models';

export type ChartOptions = {
  series: ApexNonAxisChartSeries | ApexAxisChartSeries;
  chart: ApexChart;
  responsive: ApexResponsive[];
  labels: any;
  legend: ApexLegend;
  dataLabels: ApexDataLabels;
  theme: ApexTheme,
  plotOptions: ApexPlotOptions;
  colors: Array<string>,
  options: ApexOptions,
  stroke: ApexStroke,
  tooltip: ApexTooltip,
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
};

@Component({
  selector: 'app-side-dashboard',
  templateUrl: './side-dashboard.component.html',
  styleUrls: ['./side-dashboard.component.css', '../sidebar-map.component.css']
})

export class SideDashboardComponent implements OnInit, OnChanges {

  public chartPieOptions: Partial<ChartOptions>;
  chartPie: ApexCharts;
  public chartBarOptions: Partial<ChartOptions>;
  chartBar: ApexCharts;
  public chartTreemapOptions: Partial<ChartOptions>;
  chartTreemap: ApexCharts;

  loadingPie = true;
  loadingBar = true;
  loadingTreemap = true;

  @Input() signList: Array<any> = [];
  @Input() taskList: Array<Task> = [];
  @Input() checkOptionsFilter;
  @Input() catalogFilter: CatalogFilter;
  @Input() warrantyStatus;
  @Input() retroStatus;
  @Input() signalingTypes: Array<any> = [];
  @Input() signTypeFilterFn?: (sign: any) => boolean;
  @Input() signWarrantyFilterFn?: (sign: any) => boolean;
  @Input() signRetroFilterFn?: (sign: any) => boolean;

  colorsList = [
    '#004659', '#00607A', '#00799B',
    '#59A8BE', '#B3D7E1', '#80E38A',
    '#40D54F', '#00C714', '#00B612',
    '#00A511', '#CC6200', '#E5730A',
    '#FF8515', '#FFA450', '#FFC28A',
    '#E4E4E5', '#CACACC', '#7E7E80',
    '#404042'
  ];
  taskColors = [
    '#D8B4FE', // 1. Claro (mas saturado o suficiente para não virar branco)
    '#C094F0', // 2. Lilás suave
    '#A774E2', // 3. Roxo médio claro
    '#8E54D4', // 4. Roxo "padrão" (Ponto de equilíbrio)
    '#7434C6', // 5. Roxo profundo
    '#5911B5'  // 6. Roxo escuro (Para máximo contraste)
  ];

  hvdValues = [
    { value: 'Vertical', quantity: 0, cost: 0 },
    { value: 'Horizontal', quantity: 0, cost: 0 },
    { value: 'Device', quantity: 0, cost: 0 }];

  constructor(
    private _catalogService: CatalogService,
    private currencyPipe: CurrencyPipe,
    private _rbac: RbacService,
    private _mapGoogleService: MapGoogleService,
    private _mapService: MapService,

  ) { }

  ngOnInit(): void {
    this.setChartPie();
    this.getRegionsCatalog();
    this.setChartTreeMap();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes?.taskList?.firstChange && changes?.taskList?.currentValue != changes?.taskList?.previousValue)
      this.setChartTreeMap(true);

    if (!changes?.signList?.firstChange) {
      if (changes?.warrantyStatus?.currentValue != changes?.warrantyStatus?.previousValue
        || changes?.retroStatus?.currentValue != changes?.retroStatus?.previousValue
        || changes?.signList?.currentValue != changes?.signList?.previousValue
        || changes?.catalogFilter?.currentValue != changes?.catalogFilter?.previousValue
        || changes?.signalingTypes?.currentValue != changes?.signalingTypes?.previousValue) {
        this.setChartPie(true);
        this.getRegionsCatalog(true);
      }
    }
  }

  closeSideBar() {
    this._mapGoogleService.toggleSideBar(false);
    this._mapService.openSideBar(false);
  }

  // Usando o get de regiões com filtro
  getRegionsCatalog(update = false) {
    this.loadingBar = true;
    const dashboardFilter: CatalogFilter = { ...(this.catalogFilter ?? {}) };

    if (this.warrantyStatus) {
      dashboardFilter.warrantyStatus = [this.warrantyStatus];
    } else {
      delete dashboardFilter.warrantyStatus;
    }

    if (this.retroStatus) {
      dashboardFilter.retroreflectionStatus = true;
    } else {
      delete dashboardFilter.retroreflectionStatus;
    }

    if (this.signalingTypes?.length) {
      dashboardFilter.signalingTypes = this.signalingTypes;
    }

    this._catalogService.getDashboardMapCatalogFiltered(dashboardFilter).subscribe((res: { areas: any[]; regions: any[]; total: any[] }) => {
      this.loadingBar = false;
      this.setChartBar(res.regions, update);
    })
  }

  // Cria dashboard de barras
  setChartBar(regions: Array<any>, update) {
    let regionsNotEmpty = regions.filter(r => r.vertical.quantity + r.horizontal.quantity + r.device.quantity > 0);
    let serieRegions = regionsNotEmpty.map(region => {
      return region.vertical.quantity + region.horizontal.quantity + region.device.quantity
    });
    let colorList = this.createColorList(regions);
    let w = regionsNotEmpty.length * 60;
    this.chartBarOptions = {
      series: [{
        data: serieRegions,
      }],
      chart: {
        height: '370px',
        width: w > 400 ? w : 400,
        type: 'bar',
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 4,
          distributed: true,
          barHeight: '100%'
        },

      },
      dataLabels: {
        enabled: true,
      },
      colors: colorList,

      stroke: {
        show: true,
        width: 0,
        colors: ['transparent']
      },

      xaxis: {
        categories: (regionsNotEmpty.map(r => r.name)),
        labels: {
          style: {
            fontSize: '12px',
            cssClass: 'text-dashboard-overflow',
            colors: colorList
          },
          rotate: -45,
          maxHeight: 100,
          minHeight: 60,
          trim: true,
        },
      },
      yaxis: {
        show: true,
        showAlways: true,
        showForNullSeries: true,
        seriesName: undefined,
        opposite: false,
        reversed: false,
        logarithmic: false,
        logBase: 10,
        tickAmount: 3,
        title: {
          text: 'Quantidade de Sinalizações (un.)',
          style: {
            fontSize: '14px',
            color: '#525252',
            fontFamily: 'Roboto',
            fontWeight: '400',
          }
        },
        labels: {
          style: {
            fontSize: '16px',
          },
          formatter: (value) => {
            return value?.toFixed(0);
          },
        },
      },

      tooltip: {
        custom: ({ seriesIndex, dataPointIndex }) => {
          let quantity = 0;
          let cost = 0;
          let htmlCost = '';
          quantity += regionsNotEmpty[dataPointIndex]?.vertical?.quantity + regionsNotEmpty[dataPointIndex]?.device?.quantity + regionsNotEmpty[dataPointIndex]?.horizontal?.quantity;
          cost += regionsNotEmpty[dataPointIndex]?.vertical?.cost + regionsNotEmpty[dataPointIndex]?.device?.cost + regionsNotEmpty[dataPointIndex]?.horizontal?.cost;

          if (this._rbac.can('financial', 'read')) htmlCost = `<span>${(this.currencyPipe.transform(cost, 'BRL'))} </span>`
          return `<div class="dashboard-tooltip">
                    <span>${quantity || 0} ${quantity <= 1 ? 'sinalização' : 'sinalizações'}</span>
                    ${htmlCost}
                  </div>`;
        }
      },
      legend: {
        show: false,
      }
    };
    setTimeout(() => {
      this.chartBar = new ApexCharts(document.querySelector("#chartBar"), this.chartBarOptions);
      this.chartBar.render();
    }, 50);

  }

  // Criar dashboard de pizza
  setChartPie(update = false) {
    this.loadingPie = true;
    this.hvdValues = [
      { value: 'Vertical', quantity: 0, cost: 0 },
      { value: 'Horizontal', quantity: 0, cost: 0 },
      { value: 'Device', quantity: 0, cost: 0 }];

    this.signList.forEach(sign => {
      const allowByType = this.signTypeFilterFn ? this.signTypeFilterFn(sign) : true;
      const allowByWarranty = this.signWarrantyFilterFn ? this.signWarrantyFilterFn(sign) : true;
      const allowByRetro = this.signRetroFilterFn ? this.signRetroFilterFn(sign) : true;
      if (allowByType && allowByWarranty && allowByRetro) {

        if (sign.type == SignalingTypeEnum.Vertical) {
          this.hvdValues[0].quantity += 1;
          this.hvdValues[0].cost += sign.cost;
        }
        else if (sign.type == SignalingTypeEnum.Horizontal) {
          this.hvdValues[1].quantity += 1;
          this.hvdValues[1].cost += sign.cost;
        }
        else if (sign.type == SignalingTypeEnum.Device) {
          this.hvdValues[2].quantity += 1;
          this.hvdValues[2].cost += sign.cost;
        }
      }
    });
    console.log(this.hvdValues)
    this.chartPieOptions = {
      stroke: {
        show: false,
      },
      series: this.hvdValues.map(hvd => hvd.quantity),
      colors: ['#004659', '#00799b', '#59a8be'],
      chart: {
        type: 'pie',
        width: '500px',
        background: '#red',
        toolbar: {
          show: false,
        },
        dropShadow: {
          enabled: true,
          color: '#404042',
          opacity: 1,
          blur: 1,
          left: 0,
          top: 0,
        },
      },
      legend: {
        show: false,
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 200,
              colors: ['red'],
            },
          },
        }
      ],
      labels: ['Verticais', 'Horizontais', 'Dispositivos'],
      dataLabels: {
        enabled: false,
      },
    };
    setTimeout(() => {
      this.loadingPie = false;
      setTimeout(() => {
        this.chartPie = new ApexCharts(document.querySelector("#chartPie"), this.chartPieOptions);
        this.chartPie?.render();
      }, 50);
    }, 500);
  }

  // Criar dashboard treemap
  setChartTreeMap(update = false) {
    this.loadingTreemap = true;
    const separateTaskList = [];
    separateTaskList.push(this.taskList.filter(t => t.taskType == TaskTypeEnum.Implantation));
    separateTaskList.push(this.taskList.filter(t => t.taskType == TaskTypeEnum.Removal));
    separateTaskList.push(this.taskList.filter(t => t.taskType == TaskTypeEnum.Maintenance));
    separateTaskList.push(this.taskList.filter(t => t.taskType == TaskTypeEnum.Replacement));
    separateTaskList.push(this.taskList.filter(t => t.taskType == TaskTypeEnum.Cataloging));
    separateTaskList.push(this.taskList.filter(t => t.taskType == TaskTypeEnum.Revitalizing));

    this.chartTreemapOptions = {
      series: [{
        data: [
          { x: 'Implantação', y: separateTaskList[0].length },
          { x: 'Remoção', y: separateTaskList[1].length },
          { x: 'Manutenção', y: separateTaskList[2].length },
          { x: 'Substituição', y: separateTaskList[3].length },
          { x: 'Catalogação', y: separateTaskList[4].length },
          { x: 'Revitalização', y: separateTaskList[5].length },
        ]
      }],
      chart: {
        height: '400px',
        width: '500px',
        type: 'treemap',
        toolbar: {
          show: false,
        },
      },
      stroke: {
        show: false,
      },
      colors: this.taskColors,
      plotOptions: {
        treemap: {
          distributed: true,
          enableShades: false,
        },
      },
      legend: { show: false },
      dataLabels: {
        enabled: true,
      },

      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 200,
            },
            legend: {
              position: 'bottom',
            },
          },
        },
      ],
      tooltip: {
        custom: ({ seriesIndex, dataPointIndex, w }) => {
          const taskTypeList: Array<Task> = separateTaskList[dataPointIndex];
          const taskVerticalList: Array<Task> = taskTypeList.filter(t => t.signalingType == SignalingTypeEnum.Vertical);
          const taskHorizontalList: Array<Task> = taskTypeList.filter(t => t.signalingType == SignalingTypeEnum.Horizontal);
          const taskDeviceList: Array<Task> = taskTypeList.filter(t => t.signalingType == SignalingTypeEnum.Device);

          let typeCost = '';
          let verticalCost = '';
          let horizontalCost = '';
          let deviceCost = '';

          if (this._rbac.can('financial', 'read')) {
            typeCost = `<p class="m-0" style="color: var(--green-base)">${formatCurrency(taskTypeList.reduce((acc, t) => acc + (t.amount ?? 0), 0), 'pt-BR', 'R$')}</p>`;
            verticalCost = `<p class="m-0" style="color: var(--green-base)">>${formatCurrency(taskVerticalList.reduce((acc, t) => acc + (t.amount ?? 0), 0), 'pt-BR', 'R$')}</p>`;
            horizontalCost = `<p class="m-0" style="color: var(--green-base)">${formatCurrency(taskHorizontalList.reduce((acc, t) => acc + (t.amount ?? 0), 0), 'pt-BR', 'R$')}</p>`;
            deviceCost = `<p class="m-0" style="color: var(--green-base)">${formatCurrency(taskDeviceList.reduce((acc, t) => acc + (t.amount ?? 0), 0), 'pt-BR', 'R$')}</p>`;
          }

          return `<div class="custom-dashboard-tooltip" style="background: white; padding: 8px; color: var(--gray-dark); width: 340px; height: 250px">
                    <p style="font-weight: 500; font-size: 14px; margin: 16px">${enumerators.TaskType[taskTypeList[0].taskType]}</p>
                      <div style="display: flex; align-items-center; height: 35%; padding-left: 32px">
                          <div style="display: flex; flex-direction: column; width: 50%;">
                              <p class="m-0" style="font-weight: 500;">Total de sinalizações</p>
                              <p class="m-0" style="color: var(--gray-dark)">${taskTypeList.length}</p>
                              ${typeCost}
                          </div>
                          <div style="display: flex; flex-direction: column; width: 50%;">
                              <p class="m-0"><i class="fak fa-map-vertical mr-2" style="font-size: 14px;"></i>Verticais</p>
                              <p class="m-0" style="color: var(--gray-base)">${taskVerticalList.length || 0} ${taskVerticalList.length == 1 ? ' Sinalização' : ' Sinalizações'}</p>
                              ${verticalCost}
                          </div>
                      </div>
                      <div style="display: flex; align-items: center; height: 35%; padding-left: 32px">
                          <div style="display: flex; flex-direction: column; width: 50%;">
                              <p class="m-0"><i class="fak fa-map-horizontal mr-2" style="font-size: 14px;"></i>Horizontais</p>
                              <p class="m-0" style="color: var(--gray-base)">${taskHorizontalList.length || 0} ${taskHorizontalList.length == 1 ? ' Sinalização' : ' Sinalizações'}</p>
                              ${horizontalCost}
                          </div>
                          <div style="display: flex; flex-direction: column; width: 50%;">
                              <p class="m-0"><i class="fak fa-map-devices mr-2" style="font-size: 14px;"></i>Dispositivos</p>
                              <p class="m-0" style="color: var(--gray-base)">${taskDeviceList.length || 0} ${taskDeviceList.length == 1 ? ' Sinalização' : ' Sinalizações'}</p>
                              ${deviceCost}
                          </div>
                      </div>
                  </div>`;
        },
      },
    };

    setTimeout(() => {
      this.loadingTreemap = false;
      setTimeout(() => {
        this.chartTreemap = new ApexCharts(document.querySelector("#chartTreemap"), this.chartTreemapOptions);
        this.chartTreemap.render();
      }, 50);
    }, 500);
  };


  // Cria uma lista que reinicia as cores até acabar a lista de itens
  createColorList(list) {
    let colorsDashboard = [];
    let index;
    for (let item = 0; item < list.length; item++) {
      index = item > this.colorsList.length - 1 ? (item % (this.colorsList.length)) : item;
      colorsDashboard.push(this.colorsList[index]);
    }

    index = list.length + 1 > this.colorsList.length - 1
      ? ((list.length) % (this.colorsList.length))
      : list.length;

    colorsDashboard.push(this.colorsList[index]);
    colorsDashboard.push('#f1f1f4');

    return colorsDashboard
  }
}
